import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import LayoutSelector from '../components/layouts/LayoutSelector';
import NotFoundPage from '../components/NotFoundPage';
import PublicProfileHeader from '../components/Profile/PublicProfileHeader';
import {
  PublicArticleAction,
  PublicHighlightSaveAction,
} from '../components/Profile/PublicHighlightsActions';
import { APIError, client } from '../services/apiClient';
import { useCurrentUser } from '../services/userHooks';
import { useToast } from '../hooks/useToast';
import { createTextFragmentUrl } from '../utils/textFragmentUtils';
import type { Note } from '../types/api';
import type { LayoutConfig } from '../types/layout';

export const Route = createFileRoute('/p/$id/highlights/$bookmarkId')({
  component: PublicHighlightsPage,
});

function PublicHighlightsPage() {
  const { id, bookmarkId } = Route.useParams();
  const navigate = Route.useNavigate();
  const queryClient = useQueryClient();
  const { currentUser } = useCurrentUser();
  const { showToast } = useToast();
  const [viewerBookmarkId, setViewerBookmarkId] = useState<string | null>(null);
  const [savedHighlightIds, setSavedHighlightIds] = useState<Set<string>>(new Set());
  const [savingHighlightId, setSavingHighlightId] = useState<string | null>(null);
  const { data, isLoading, error } = useQuery({
    queryKey: ['public-highlights', id, bookmarkId],
    queryFn: () => client.publicHighlights(id, bookmarkId),
    retry: (failureCount, queryError) =>
      queryError instanceof APIError && queryError.status === 404
        ? false
        : failureCount < 1,
  });
  const layoutConfig: LayoutConfig = { type: 'hybrid', showAuthButtons: true };
  const returnPath = `/p/${id}/highlights/${bookmarkId}`;
  const authUrl = `/user/login?next=${encodeURIComponent(returnPath)}`;
  const isOwner = Boolean(
    currentUser && data && String(currentUser.id) === String(data.user.id),
  );

  useEffect(() => {
    setViewerBookmarkId(data?.viewer_bookmark_id ?? null);
    setSavedHighlightIds(new Set(data?.viewer_saved_highlight_ids ?? []));
  }, [data?.viewer_bookmark_id, data?.viewer_saved_highlight_ids]);

  useEffect(() => {
    if (!data) return;

    const name = data.user.display_name || data.user.username || id;
    const handle = data.user.username ? `@${data.user.username}` : '';
    const owner =
      handle && handle.toLowerCase() !== name?.toLowerCase()
        ? `${name} (${handle})`
        : name;
    const count = data.notes.length;
    const articleTitle = data.bookmark.title;
    const articleSource = data.bookmark.site_name || data.bookmark.url;
    const countLabel =
      count === 1 ? t`1 public highlight` : t`${count} public highlights`;
    const pageTitle = t`${articleTitle} — ${owner} on Instapaper`;
    const description = t`${countLabel} from ${owner} on ${articleSource}.`;
    const imageUrl = new URL(data.preview_image_url, window.location.origin).href;

    document.title = pageTitle;
    const meta = {
      'og:title': pageTitle,
      'og:description': description,
      'og:image': imageUrl,
      'twitter:title': pageTitle,
      'twitter:description': description,
      'twitter:image': imageUrl,
    };
    Object.entries(meta).forEach(([key, content]) => {
      const attribute = key.startsWith('og:') ? 'property' : 'name';
      let element = document.head.querySelector<HTMLMetaElement>(
        // eslint-disable-next-line lingui/no-unlocalized-strings -- DOM selector.
        `meta[${attribute}="${key}"]`,
      );
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.content = content;
    });
  }, [data, id]);

  async function ensureArticleSaved(): Promise<string> {
    if (viewerBookmarkId) {
      return viewerBookmarkId;
    }
    if (!data) {
      throw new Error('Public highlights are not loaded');
    }

    const bookmark = await client.addBookmark(data.bookmark.url);
    const savedBookmarkId = String(bookmark.id);
    setViewerBookmarkId(savedBookmarkId);
    return savedBookmarkId;
  }

  const saveArticle = useMutation({
    mutationFn: ensureArticleSaved,
    onSuccess: async () => {
      showToast(t`Saved to Instapaper`, 'success');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bookmarks'] }),
        queryClient.invalidateQueries({ queryKey: ['currentUser'] }),
      ]);
    },
    onError: () => {
      showToast(t`Could not save the article. Please try again.`, 'error');
    },
  });

  const saveHighlight = useMutation({
    mutationFn: async (note: Note) => {
      const savedBookmarkId = await ensureArticleSaved();
      await client.createHighlight(savedBookmarkId, {
        text: note.text,
        note: note.note,
        position: note.position,
      });
      return note.id;
    },
    onMutate: (note) => {
      setSavingHighlightId(note.id);
    },
    onSuccess: async (noteId) => {
      setSavedHighlightIds((previous) => new Set(previous).add(noteId));
      showToast(t`Highlight saved`, 'success');
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['bookmarks'] }),
        queryClient.invalidateQueries({ queryKey: ['currentUser'] }),
      ]);
    },
    onError: () => {
      showToast(t`Could not save the highlight. Please try again.`, 'error');
    },
    onSettled: () => {
      setSavingHighlightId(null);
    },
  });

  const stopSharing = useMutation({
    mutationFn: () => client.stopSharingHighlights(bookmarkId),
    onSuccess: async () => {
      showToast(t`Public link disabled`, 'success');
      await queryClient.invalidateQueries({ queryKey: ['profile', id] });
      void navigate({ to: '/p/$id', params: { id } });
    },
    onError: () => {
      showToast(t`Something went wrong.`, 'error');
    },
  });

  if (error instanceof APIError && error.status === 404) {
    return <NotFoundPage />;
  }

  return (
    <LayoutSelector config={layoutConfig} title={data?.bookmark.title || t`Highlights`}>
      <div className="bookmark-list-page-layout">
        <div className="bookmark-list-page-list-area">
          <PublicProfileHeader
            userId={id}
            user={data?.user}
            publicFolders={data?.public_folders}
            publicHighlights={data?.public_highlights}
            selectedHighlightBookmarkId={bookmarkId}
            showSeparator
          />
          <main className="bookmark-list-content bg-surface-primary min-h-0 flex-1">
            <div className="bookmark-list-scroll-container px-5 py-10 md:px-10">
              <article className="mx-auto max-w-2xl">
                {isLoading ? (
                  <p className="text-copy-secondary">
                    <Trans>Loading...</Trans>
                  </p>
                ) : error || !data ? (
                  <p className="text-copy-secondary">
                    <Trans>Sorry, there was an error. Please try again.</Trans>
                  </p>
                ) : (
                  <>
                    <header className="mb-10">
                      <h2 className="text-copy-primary font-lyon-display mb-1 text-3xl leading-tight">
                        {data.bookmark.title}
                      </h2>
                      <a
                        href={data.bookmark.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-copy-secondary hover:text-link-hover font-inter text-sm no-underline transition-colors"
                      >
                        {data.bookmark.site_name || data.bookmark.url}
                      </a>
                      <div className="mt-4">
                        <PublicArticleAction
                          authUrl={authUrl}
                          isOwner={isOwner}
                          isLoggedIn={Boolean(currentUser)}
                          isSaved={Boolean(viewerBookmarkId)}
                          isPending={
                            isOwner ? stopSharing.isPending : saveArticle.isPending
                          }
                          onSave={() => saveArticle.mutate()}
                          onStopSharing={() => stopSharing.mutate()}
                        />
                      </div>
                    </header>

                    <div className="space-y-10">
                      {data.notes.map((note) => (
                        <section key={note.id}>
                          <a
                            href={createTextFragmentUrl(data.bookmark.url, note.text)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="note-item font-article break-words whitespace-pre-wrap"
                          >
                            <blockquote className="note-item-quote">
                              {note.text}
                            </blockquote>
                          </a>
                          {note.note && (
                            <p className="text-copy-primary mt-3 text-base leading-7 whitespace-pre-wrap">
                              {note.note}
                            </p>
                          )}
                          <div className="mt-3 flex">
                            <PublicHighlightSaveAction
                              authUrl={authUrl}
                              isOwner={isOwner}
                              isLoggedIn={Boolean(currentUser)}
                              isSaved={savedHighlightIds.has(note.id)}
                              isPending={savingHighlightId === note.id}
                              onSave={() => saveHighlight.mutate(note)}
                            />
                          </div>
                        </section>
                      ))}
                    </div>
                  </>
                )}
              </article>
            </div>
          </main>
        </div>
      </div>
    </LayoutSelector>
  );
}
